import { ErrorMessage } from '@hookform/error-message';
import { type FC, useEffect, useState } from 'react';
import {
  Accordion,
  Button,
  Col,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip,
} from 'react-bootstrap';
import { Clipboard, EyeFill, Plus, Trash } from 'react-bootstrap-icons';
import Select from 'react-dropdown-select';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import type { Option } from 'react-multi-select-component';

import './styles/styles.css';

import {
  useAuth,
  useManualQueryRequest,
  useMutationRequest,
  useNotifications,
} from '../../../modules/hooks';

import { endpoints } from '../../../modules/mappers/urls';
import {
  copyWhatsappTemplate,
  replaceTemplateVariablesWithIndices,
} from '../../../modules/utils/copyTemplates';
import { MessagePreviewModal } from './MessagePreviewModal';
import { SendNowModal } from './SendNowModal';

export interface ModalComponentData {
  show: boolean;
  handleClose: () => void;
  isEdit?: boolean;
  defaultTemplate?: WhatsAppTemplate;
  propertiesAddFunction?: () => void;
  propertyId: string;
  reservationId?: string;
}

type KeyValueItem = {
  key: string;
  value: string;
};

export interface WhatsAppTemplate {
  uuid?: string;
  templateId?: string;
  subject: string;
  message?: string;
  description?: string;
  skip?: boolean;
  fieldValues?: Record<string, string>;
  entries?: KeyValueItem[];
  autoInjectedRenderFields?: Record<string, string>;
  template?: WhatsAppTemplate;
  defaultSmsTemplate?: string;
  whatsappTemplateId?: string;
}

export interface SelectType {
  label: string;
  value: string;
  message?: string;
}

export const AutomaticMessagesManagerModal: FC<ModalComponentData> = ({
  show,
  handleClose,
  isEdit = false,
  defaultTemplate,
  propertyId,
  reservationId,
  propertiesAddFunction,
}) => {
  const [templateIdsList, setTemplateIdsList] = useState<WhatsAppTemplate[]>([]);
  const [templateReservationList, setTemplateReservationList] = useState<
    WhatsAppTemplate[]
  >([]);
  const { setSimpleToasts } = useNotifications()!;
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [showSendNowModal, setShowSendNowModal] = useState<boolean>(false);
  const [whatsAppTemplateId, setWhatsAppTemplateId] = useState<string>('');
  const [combinedVariables, setCombinedVariables] = useState({});
  const [autoInjectedFields, setAutoInjectedFields] = useState<KeyValueItem[]>([]);
  const [templateMessage, setTemplateMessage] = useState<string>('');
  const [templateId, setTemplateId] = useState<string>('');
  const { credentialsInfo } = useAuth()!;

  const [selectedTemplate, setSelectedTemplate] = useState<Option[]>([]);
  const {
    control,
    register,
    handleSubmit,
    reset,
    trigger,
    getValues,
    setValue,
    watch,
    setError,

    formState: { errors },
  } = useForm<WhatsAppTemplate>({
    mode: 'onChange',
    defaultValues: {
      templateId: '',
      subject: '',
      message: '',
      skip: false,
      entries: [{ key: '', value: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'entries',
  });

  const watchedEntries = watch('entries');

  const lastEntry = (watchedEntries?.[watchedEntries.length - 1] || {}) as {
    key?: string;
    value?: string;
  };
  const isLastEntryFilled = lastEntry.key?.trim() && lastEntry.value?.trim();

  const isSkipped = watch('skip');

  const getPropertyUrl = `${endpoints.PROPERTIES}/${propertyId}/messaging-templates`;
  const [
    { data: propertyTemplatesResult, loading: loadingPropertyTemplates },
    getPropertyTemplates,
  ] = useManualQueryRequest<WhatsAppTemplate[]>(getPropertyUrl, {
    authToken: credentialsInfo?.token,
  });

  const propertyTemplateUrl = `${endpoints.PROPERTIES}/${propertyId}/messaging-templates/${templateId}`;

  const [
    { data: addPropertyTemplateData, loading: loadingAddPropertyTemplateData },
    addMessagingTemplate,
  ] = useMutationRequest(propertyTemplateUrl, 'patch', {
    authToken: credentialsInfo?.token,
  });

  const reservationTemplateUrl = `${endpoints.RESERVATION}/${reservationId}/messaging-templates/${templateId}`;

  const [
    { data: addReservationTemplateData, loading: loadingAddReservationTemplateData },
    addReservationMessagingTemplate,
  ] = useMutationRequest(reservationTemplateUrl, 'patch', {
    authToken: credentialsInfo?.token,
  });

  const getReservationUrl = `${endpoints.RESERVATION}/${reservationId}/messaging-templates`;
  const [
    { data: reservationTemplatesResult, loading: loadingReservationTemplates },
    getReservationTemplates,
  ] = useManualQueryRequest<WhatsAppTemplate[]>(getReservationUrl, {
    authToken: credentialsInfo?.token,
  });

  const onSkipAutomaticMessage = (editorState: boolean) => {
    setValue('skip', editorState);
  };

  const selectProperties = (values: Option[]) => {
    if (!isEdit) {
      setSelectedTemplate(values);

      const selectedId = values?.[0]?.value || '';
      if (selectedId) setTemplateId(selectedId);

      const messageTemp = templateReservationList.find(
        (item) => item?.template?.templateId === getValues('templateId'),
      );

      const message =
        messageTemp?.message || messageTemp?.template?.defaultSmsTemplate || '';

      const fieldValues =
        messageTemp?.fieldValues || messageTemp?.template?.fieldValues || {};

      const autoInjectedValues =
        messageTemp?.autoInjectedRenderFields ||
        messageTemp?.template?.autoInjectedRenderFields ||
        {};

      setValue('message', message);
      setValue('skip', !!messageTemp?.skip);
      setValue(
        'entries',
        Object.entries(fieldValues).length
          ? Object.entries(fieldValues).map(([key, value]) => ({ key, value }))
          : [{ key: '', value: '' }],
      );
      setWhatsAppTemplateId(
        messageTemp?.whatsappTemplateId ||
          messageTemp?.template?.whatsappTemplateId ||
          '',
      );
      setTemplateMessage(message);
      setAutoInjectedFields(
        Object.entries(autoInjectedValues).map(([key, value]) => ({
          key,
          value,
        })),
      );

      if (Object.keys(fieldValues).length) {
        setCombinedVariables(fieldValues);
      }
    }
  };

  const getTemplateIdsDropDownData = () => {
    const dropDownList: SelectType[] = [];

    templateIdsList.forEach((item) => {
      dropDownList.push({
        value: item?.templateId || item?.template?.templateId || '',
        label: item.subject,
        message: item.message,
      });
    });
    return dropDownList;
  };

  const convertFieldValuesToObject = (entries?: KeyValueItem[]) => {
    let fieldValues: Record<string, string> = {};
    if (entries?.length) {
      fieldValues = entries?.reduce(
        (acc, { key, value }) => {
          if (key?.trim()) {
            acc[key.trim()] = value;
          }
          return acc;
        },
        {} as Record<string, string>,
      );
    }
    return fieldValues;
  };

  const AddRequestHandler = async (data: WhatsAppTemplate) => {
    const fieldValues: Record<string, string> = convertFieldValuesToObject(data?.entries);

    const { subject, message, skip } = data;
    const templateValues: WhatsAppTemplate = {
      subject,
      skip,
      fieldValues,
      message,
    };

    if (propertiesAddFunction) {
      addMessagingTemplate(templateValues);
    } else {
      addReservationMessagingTemplate(templateValues);
    }
  };

  function validateMessageVariables(message: string, entries: KeyValueItem[]): boolean {
    const fieldValues: Record<string, string> = {};
    (entries || []).forEach(({ key, value }) => {
      if (key.trim()) {
        fieldValues[key.trim()] = value;
      }
    });

    // search for variables between {{ }}
    const variableRegex = /{{\s*([\w.-]+)\s*}}/g;
    const messageVariables = new Set<string>();

    let match: RegExpExecArray | null;

    for (
      match = variableRegex.exec(message);
      match !== null;
      match = variableRegex.exec(message)
    ) {
      messageVariables.add(match[1]);
    }

    // variables missing in the message
    const missingVariables = Array.from(messageVariables).filter(
      (variable) => !(variable in fieldValues),
    );

    // variables no used in the message
    const unusedFields = Object.keys(fieldValues).filter(
      (fieldKey) => !messageVariables.has(fieldKey),
    );

    if (missingVariables.length > 0 || unusedFields.length > 0) {
      const errorMessages: string[] = [];
      if (missingVariables.length > 0) {
        errorMessages.push(
          `-The following fields are present in the template field values list but missing in the message template:::${missingVariables.join(
            ', ',
          )}`,
        );
      }
      if (unusedFields.length > 0) {
        errorMessages.push(
          `-The following fields are missing from the template field values list but used in the message template:::${unusedFields.join(
            ', ',
          )}`,
        );
      }

      setError('message', {
        type: 'manual',
        message: errorMessages.join('\n'),
      });
      return false;
    }

    return true;
  }

  const onSubmit = async (data: WhatsAppTemplate) => {
    if (!validateMessageVariables(data.message || '', data.entries || [])) return;
    const validated = await trigger(['message', 'templateId']);

    if (validated) {
      AddRequestHandler(data);
    }
  };

  const copyFieldValueToClipboard = (index: number) => {
    const keyValue = getValues(`entries.${index}.key`); // usa react-hook-form para obtener el valor
    if (!keyValue) {
      setSimpleToasts?.({
        type: 'warning',
        message: 'No key value to copy',
        show: true,
      });
      return;
    }

    const formattedValue = `{{${keyValue}}}`;
    navigator.clipboard.writeText(formattedValue);

    setSimpleToasts?.({
      type: 'success',
      message: `Copied ${formattedValue} to clipboard`,
      show: true,
    });
  };

  const sendNowHandler = () => {
    setShowSendNowModal(true);
  };

  const onClose = () => {
    handleClose();
    reset({
      templateId: '',
      subject: '',
      message: '',
      skip: false,
      entries: [{ key: '', value: '' }],
    });
    setTemplateMessage('');

    setTemplateId('');
    setSelectedTemplate([]);
  };

  useEffect(() => {
    if (!isEdit && show) {
      getPropertyTemplates();
      getReservationTemplates();
    }
  }, [isEdit, show]);

  useEffect(() => {
    if (isEdit) return;

    if (propertyTemplatesResult) {
      setTemplateIdsList(propertyTemplatesResult.result);
    }

    if (reservationTemplatesResult) {
      setTemplateReservationList(reservationTemplatesResult.result);
    }
  }, [propertyTemplatesResult, reservationTemplatesResult]);

  useEffect(() => {
    if (addPropertyTemplateData) {
      onClose();
      if (propertiesAddFunction) {
        propertiesAddFunction();
      }
    }

    if (addReservationTemplateData) {
      onClose();
    }
  }, [addPropertyTemplateData, addReservationTemplateData]);

  useEffect(() => {
    if (isEdit && defaultTemplate && show) {
      const defaultMessage =
        defaultTemplate?.message || defaultTemplate.template?.defaultSmsTemplate || '';

      const defaultFieldValues =
        defaultTemplate?.fieldValues || defaultTemplate?.template?.fieldValues || {};

      const transformedFields = Object.entries(defaultFieldValues).map(
        ([key, value]) => ({ key, value }),
      );

      const autoInjectedValues = defaultTemplate?.autoInjectedRenderFields || {};

      const transformedAutoInjectedFields = Object.entries(autoInjectedValues).map(
        ([key, value]) => ({ key, value }),
      );

      reset({
        templateId: defaultTemplate?.template?.templateId,
        subject: defaultTemplate.subject,
        message: defaultMessage,
        skip: defaultTemplate.skip,
        entries:
          transformedFields.length > 0 ? transformedFields : [{ key: '', value: '' }],
      });

      setTemplateIdsList([defaultTemplate]);

      const templates = [
        {
          value: defaultTemplate?.template?.templateId,
          label: defaultTemplate.subject,
        },
      ];

      setWhatsAppTemplateId(
        defaultTemplate?.whatsappTemplateId ||
          defaultTemplate?.template?.whatsappTemplateId ||
          '',
      );

      setAutoInjectedFields(transformedAutoInjectedFields);
      setSelectedTemplate(templates);
      setCombinedVariables(defaultFieldValues);
      setTemplateId(defaultTemplate?.template?.templateId || '');
      setTemplateMessage(defaultMessage);
    }
  }, [isEdit, defaultTemplate, show]);

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static" keyboard={false}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header>
          <Modal.Title className="editor-title">Automations Manager</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {templateIdsList.length !== 0 && (
              <Row className="mt-8px">
                <Form.Group className="border-bottom">
                  <Form.Label className="editor-title-label">Message Template</Form.Label>
                  <Controller
                    name="templateId"
                    control={control}
                    rules={{ required: 'This is required.' }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={getTemplateIdsDropDownData()}
                        values={selectedTemplate}
                        onChange={(values) => {
                          field.onChange(values[0]?.value || '');
                          selectProperties(values);
                        }}
                        disabled={isEdit}
                        loading={loadingReservationTemplates || loadingPropertyTemplates}
                      />
                    )}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="templateId"
                    render={({ message }) => (
                      <span className="error-text">{message}</span>
                    )}
                  />
                </Form.Group>
              </Row>
            )}

            <Row className="mt-24px">
              <Col sm={12}>
                <Form.Label>Message</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  {...register('message', { required: 'This is required' })}
                  disabled={!templateId}
                />
                <ErrorMessage
                  errors={errors}
                  name="message"
                  render={({ message }) => {
                    const messages = message.split('\n');
                    return (
                      <span className="error-text">
                        {messages.map((msg, idx) => {
                          const [text, variables] = msg.split(':::');
                          return (
                            <div key={idx}>
                              {text}: {variables && <strong>{variables}</strong>}
                            </div>
                          );
                        })}
                      </span>
                    );
                  }}
                />
              </Col>
            </Row>

            {whatsAppTemplateId && (
              <Row className="mt-24px">
                <Col>
                  <Form.Label>Whatsapp Template ID</Form.Label>
                </Col>
                <Col sm={11}>
                  <Form.Control value={whatsAppTemplateId} readOnly />
                </Col>
                <Col xs={1} style={{ display: 'flex', justifyContent: 'right' }}>
                  <Button
                    variant="outline-secondary"
                    onClick={() =>
                      copyWhatsappTemplate(setSimpleToasts, whatsAppTemplateId)
                    }
                    type="button"
                  >
                    <Clipboard />
                  </Button>
                </Col>
              </Row>
            )}

            <Row>
              <Col sm={8} className="mt-24px">
                <Form.Check
                  type="checkbox"
                  checked={isSkipped}
                  onChange={(e) => onSkipAutomaticMessage(e.currentTarget.checked)}
                  label="Skip Automation"
                  disabled={!templateId}
                />
              </Col>

              <Col
                sm={4}
                className="mt-24px"
                style={{ display: 'flex', justifyContent: 'right' }}
              >
                <OverlayTrigger
                  overlay={<Tooltip>Copy WhatsApp template to clipboard </Tooltip>}
                >
                  <Button
                    style={{
                      padding: '7px',
                      marginLeft: 10,
                    }}
                    onClick={() => {
                      replaceTemplateVariablesWithIndices(
                        setSimpleToasts,
                        getValues('message'),
                      );
                    }}
                    disabled={!templateId}
                  >
                    <Clipboard size={15} />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger overlay={<Tooltip>Preview</Tooltip>}>
                  <Button
                    style={{
                      padding: '7px',
                      marginLeft: 10,
                      backgroundColor: '#232323',
                    }}
                    onClick={() => {
                      const message = getValues('message');
                      if (message) {
                        setTemplateMessage(message);
                      }

                      setCombinedVariables(
                        convertFieldValuesToObject(getValues('entries')),
                      );

                      setShowPreview(true);
                    }}
                    disabled={!templateId}
                  >
                    <EyeFill size={15} />
                  </Button>
                </OverlayTrigger>
              </Col>
            </Row>

            {templateId && (
              <>
                {autoInjectedFields.length > 0 && (
                  <div className="mt-24px">
                    <Accordion>
                      <Accordion.Item eventKey="0">
                        <Accordion.Header>Available Fields</Accordion.Header>
                        <Accordion.Body>
                          {autoInjectedFields.map(({ key, value }) => {
                            const isSelected = watchedEntries?.some(
                              (entry) => entry.key === key,
                            );

                            return (
                              <Row key={key} className="mb-2">
                                <Col xs={5}>
                                  <Form.Control readOnly value={key} />
                                </Col>
                                <Col xs={6}>
                                  <Form.Control readOnly value={value} />
                                </Col>
                                <Col
                                  xs={1}
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Form.Check
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        append({ key, value });
                                      } else {
                                        const indexToRemove = watchedEntries?.findIndex(
                                          (entry) => entry.key === key,
                                        );
                                        if (indexToRemove !== -1) remove(indexToRemove);
                                      }
                                    }}
                                  />
                                </Col>
                              </Row>
                            );
                          })}
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </div>
                )}

                <div className="mt-24px">
                  <Form.Label className="mb-16px">Template Field Values</Form.Label>
                  {fields.map((fieldValue, index) => {
                    const isAutoInjected = autoInjectedFields.some(
                      (f) => f.key === fieldValue.key,
                    );
                    return (
                      <Row key={fieldValue.id} className="mb-2">
                        <Col xs={5}>
                          <Controller
                            control={control}
                            name={`entries.${index}.key`}
                            render={({ field }) => (
                              <Form.Control
                                placeholder="Key (optional)"
                                {...field}
                                disabled={isAutoInjected}
                              />
                            )}
                          />
                        </Col>
                        <Col xs={5}>
                          <Controller
                            control={control}
                            name={`entries.${index}.value`}
                            render={({ field }) => (
                              <Form.Control
                                placeholder="Value"
                                {...field}
                                disabled={isAutoInjected}
                              />
                            )}
                          />
                        </Col>
                        <Col xs={1} style={{ display: 'flex', justifyContent: 'right' }}>
                          {index === fields.length - 1 ? (
                            <Button
                              variant="success"
                              onClick={() => append({ key: '', value: '' })}
                              type="button"
                              disabled={!isLastEntryFilled}
                            >
                              <Plus />
                            </Button>
                          ) : (
                            <Button
                              variant="danger"
                              onClick={() => remove(index)}
                              type="button"
                              disabled={isAutoInjected}
                            >
                              <Trash />
                            </Button>
                          )}
                        </Col>
                        <Col xs={1} style={{ display: 'flex', justifyContent: 'right' }}>
                          <Button
                            variant="outline-secondary"
                            onClick={() => copyFieldValueToClipboard(index)}
                            type="button"
                          >
                            <Clipboard />
                          </Button>
                        </Col>
                      </Row>
                    );
                  })}
                </div>
                {!isEdit && (
                  <Row className="mt-24px">
                    <Col style={{ display: 'flex', justifyContent: 'right' }}>
                      <Button className="save-button" onClick={sendNowHandler}>
                        Send Now
                      </Button>
                    </Col>
                  </Row>
                )}
              </>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer className="mt-48px">
          <Button className="cancel-button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="save-button"
            type="submit"
            disabled={loadingAddReservationTemplateData || loadingAddPropertyTemplateData}
          >
            {loadingAddReservationTemplateData || loadingAddPropertyTemplateData
              ? 'Loading...'
              : 'Save'}
          </Button>
        </Modal.Footer>
      </form>

      {reservationId && templateId && (
        <SendNowModal
          reservationId={reservationId}
          templateId={templateId}
          show={showSendNowModal}
          handleClose={() => setShowSendNowModal(false)}
        />
      )}

      {templateMessage && combinedVariables && (
        <MessagePreviewModal
          show={showPreview}
          handleClose={() => setShowPreview(false)}
          message={templateMessage}
          variables={combinedVariables}
        />
      )}
    </Modal>
  );
};
